import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  useGetTableData2,
  useDeleteData,
  useCreateData,
  usePutData,
} from "../../Plan/tableHooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  localizationTextObjectForInformation,
  itemClickFirst,
  completeAdd,
} from "../../../../utils/constants";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import Pen01 from "../../Nucleo-Icons/pen-01";
import Trash from "../../Nucleo-Icons/trash";
import StackPerspective3 from "../../Nucleo-Icons/stack-perspective-3";
import { toast } from "react-toastify";
import CreateProcessDialog from "./CreateProcessDialog";
import SearchIcon from "@mui/icons-material/Search";
import { AddButton } from "../../../common/Buttons";
import { useParams } from "react-router-dom";
import goToHelpLink from "../../../../utils/goToHelpLink";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditRowDialog from "./EditRowDialog";

function Process({
  clickedItemId,
  clickedProcId,
  setClickedProcId,
  onShowConfirmation,
  orderData,
  openSnackbar,
}) {
  console.log(`clickedItemId : ${clickedItemId}`);
  const { group_code, project_code } = useParams();
  const queryClient = useQueryClient();
  const [updateStatus, setUpdateStatus] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [procSearchText, setProcSearchText] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({
    proc_name: true,
    proc_id: false,
    mch_qty: true,
    proc_time: true,
  });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const { procData, isLoading, isFetching } = useGetTableData2(
    updateStatus,
    `/api/proc/getList?group_code=${group_code}&project_code=${project_code}`,
    "procData"
  );
  const updateProcess = usePutData(
    updateStatus,
    "/api/proc/update",
    openSnackbar
  );
  const updateSequence = usePutData(
    updateStatus,
    "/api/proc/updateSequence",
    openSnackbar
  );
  const createProcess = useCreateData(
    updateStatus,
    "/api/proc/create",
    openSnackbar
  );
  const deleteProcess = useDeleteData(
    updateStatus,
    "/api/proc/delete",
    openSnackbar
  );
  useEffect(() => {
    if (procData) {
      setTableData(
        procData
          .filter((i) => i.item_id === clickedItemId)
          .sort((a, b) => a.proc_sequence_no - b.proc_sequence_no)
      );
    }
  }, [procData, clickedItemId]);
  const filteredData = useMemo(() => {
    if (!tableData) return [];
    if (!procSearchText.trim()) return tableData;
    const lowerCaseSearchText = procSearchText.toLowerCase();
    return tableData.filter((item) => {
      return (
        item.proc_name?.toLowerCase().includes(lowerCaseSearchText) ||
        String(item.proc_time).toLowerCase().includes(lowerCaseSearchText) ||
        String(item.mch_qty).toLowerCase().includes(lowerCaseSearchText) ||
        String(item.batch_size).toLowerCase().includes(lowerCaseSearchText)
      );
    });
  }, [tableData, procSearchText]);
  const columns = useMemo(
    () => [
      {
        accessorKey: "proc_name",
        header: "공정명",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
        size: 80,
      },
      {
        accessorKey: "proc_uph",
        header: "UPH",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
        size: 50,
      },
      {
        accessorKey: "proc_time",
        header: "공정시간",
        Cell: ({ row }) => {
          const minutes = row.original.proc_time / 60;
          const isInteger = minutes % 1 === 0;
          return `${isInteger ? minutes : minutes.toFixed(1)}분`;
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
        size: 50,
      },
      // {
      //   accessorKey: "mch_qty",
      //   header: "총 장비대수",
      //   muiTableHeadCellProps: { align: "center" },
      //   muiTableBodyCellProps: { align: "center" },
      //   muiTableFooterCellProps: { align: "center" },
      //   size: 80,
      //   Cell: ({ row }) => {
      //     return row.original.mch_qty + " 대";
      //   },
      //   enableEditing: false,
      // },
      // {
      //   accessorKey: "batch_size",
      //   header: "배치사이즈",
      //   muiTableHeadCellProps: { align: "center" },
      //   muiTableBodyCellProps: { align: "center" },
      //   muiTableFooterCellProps: { align: "center" },
      //   size: 80,
      //   enableEditing: true,
      // },
      {
        accessorKey: "proc_id",
        header: "",
        enableHiding: false,
      },
    ],
    []
  );
  const onItemSearchTextChange = useCallback((event) => {
    setProcSearchText(event.target.value);
  }, []);
  const handleUpdateProcess = async ({ values, table, row }) => {
    const originalProcId = values.proc_id;
    const notValidation =
      !values.proc_time || !values.proc_name || isNaN(values.proc_time);
    if (!notValidation) {
      if (Number(values.batch_size) <= 0 || isNaN(Number(values.batch_size))) {
        openSnackbar("배치사이즈는 1 이상 입력해 주세요.", "warning");
        return;
      }
      if (values.proc_name.length > 15) {
        openSnackbar("공정명은 15자 이내로 입력해 주세요.", "warning");
        return;
      }
      const updateData = [
        clickedItemId,
        originalProcId,
        values.proc_time,
        values.proc_name,
        Number(values.batch_size),
        values.uph,
        group_code,
        project_code,
      ];
      await updateProcess.mutateAsync(updateData);
      table.setEditingRow(null);
    } else {
      openSnackbar(
        "빈칸 없이 입력해 주세요. 공정시간은 숫자만 입력할 수 있습니다.",
        "warning"
      );
      return;
    }
  };
  const handleCreateProcess = async ({ values, table }) => {
    const notValidation =
      !values.proc_name || !values.proc_time || isNaN(values.proc_time);
    if (!notValidation) {
      const matchedOrder = orderData
        .filter((c) => c.item_id === clickedItemId && c.status !== 3)
        .map((i) => i.order_name);
      // const deleteArr = orderData
      //   .filter((c) => c.item_id === clickedItemId && c.status === 2)
      //   .map((c) => c.order_id);
      if (values.proc_name.length > 15) {
        openSnackbar("공정명은 15자 이내로 입력해 주세요.", "warning");
        return;
      }
      const mainText = matchedOrder.length
        ? `${matchedOrder} 생산계획이 초기화 처리됩니다.`
        : "공정 정보를 추가합니다.";
      onShowConfirmation({
        title: "공정 추가",
        mainText: mainText,
        subText: "",
        onConfirm: async () => {
          try {
            const createData = [
              clickedItemId,
              values.proc_name,
              values.proc_time,
              Number(values.batch_size),
              values.uph,
              group_code,
              project_code,
            ];
            await createProcess.mutateAsync(createData);
            table.setCreatingRow(null);
          } catch (error) {
            console.error("Error creating process:", error);
          }
        },
      });
    } else {
      toast.error(
        "빈칸 없이 입력해 주세요. 공정시간은 숫자만 입력할 수 있습니다."
      );
      return;
    }
  };
  const openDeleteConfirm = async (procId) => {
    const matchedOrder = orderData
      .filter((c) => c.item_id === clickedItemId && c.status !== 3)
      .map((i) => i.order_name);
    const mainText = matchedOrder.length
      ? `${matchedOrder} 생산계획이 초기화 처리되며, 공정의 하위 장비 정보가 모두 삭제됩니다.`
      : "공정 정보를 삭제합니다. 이 작업을 진행하면 공정의 하위 장비 정보 또한 모두 삭제됩니다.";
    onShowConfirmation({
      title: "공정 삭제",
      mainText: mainText,
      subText: "이 작업은 되돌릴 수 없습니다. 진행 하시겠습니까?",
      onConfirm: async () => {
        try {
          await deleteProcess.mutateAsync({
            itemId: clickedItemId,
            procId: procId,
            group_code,
            project_code,
          });
          // deleteArr.length &&
          //   deleteInsightwinOrderDataCompleteForInformation(deleteArr);
          setUpdateStatus((prev) => prev + 1);
        } catch (error) {
          console.error("Error deleting item:", error);
        }
      },
    });
  };
  const table = useMaterialReactTable(
    {
      data: filteredData ?? [],
      columns,
      state: {
        isLoading,
        showProgressBars: isFetching,
        columnVisibility,
        pagination,
      },
      onColumnVisibilityChange: { setColumnVisibility },
      enableColumnFilters: false,
      onPaginationChange: setPagination,
      enableColumnFilterModes: false,
      enableColumnOrdering: false,
      enableSorting: false,
      enableStickyHeader: true,
      enableColumnActions: false,
      enableEditing: true,
      createDisplayMode: "modal",
      editDisplayMode: "modal",
      autoResetPageIndex: false,
      manualPagination: true,
      enableFullScreenToggle: false,
      enableDensityToggle: false,
      enableRowOrdering: true,
      muiRowDragHandleProps: ({ row, table }) => {
        const currentPageRows = table.getRowModel().rows;
        const isFirstRowOnPage = currentPageRows[0]?.id === row.id;
        const isLastRowOnPage =
          currentPageRows[currentPageRows.length - 1]?.id === row.id;
        const isSingleRowOnPage = currentPageRows.length === 1;
        return {
          sx: {
            width: "30px",
            height: "30px",
            textAlign: "center",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            "& svg": {
              display: "none",
            },
            "::before": {
              content: () => {
                if (isSingleRowOnPage) {
                  return "url(/icons/cursor-not-allowed.svg)";
                }
                if (isFirstRowOnPage) {
                  return "url(/icons/arrow-down-from-line.svg)";
                }
                if (isLastRowOnPage) {
                  return "url(/icons/arrow-up-from-line.svg)";
                }
                return "url('/icons/arrows-from-line-y.svg')";
              },
              width: "30px",
              height: "30px",
            },
          },
          onDragEnd: async () => {
            const { draggingRow, hoveredRow } = table.getState();
            if (hoveredRow && draggingRow) {
              const newData = [...tableData];
              newData.splice(
                hoveredRow.index,
                0,
                newData.splice(draggingRow.index, 1)[0]
              );
              const arr = newData.map((c, i) => {
                return {
                  item_id: c.item_id,
                  proc_id: c.proc_id,
                  proc_sequence_no: i + 1,
                  group_code,
                  project_code,
                };
              });
              await updateSequence.mutateAsync(arr, {
                onSuccess: () => {
                  openSnackbar("공정의 순서가 변경되었습니다.");
                },
              });
            }
          },
        };
      },
      renderRowActions: ({ row, table }) => (
        <Box sx={{ display: "flex" }}>
          <Tooltip title="Edit">
            <IconButton
              onClick={() => {
                table.setEditingRow(row);
              }}
            >
              <Pen01 />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={async () => {
                await openDeleteConfirm(row.original.proc_id);
                queryClient.invalidateQueries([updateStatus]);
              }}
            >
              <Trash />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      localization: localizationTextObjectForInformation,
      onCreatingRowSave: handleCreateProcess,
      onEditingRowSave: handleUpdateProcess,
      muiTableBodyRowProps: ({ row }) => ({
        onClick: () => {
          setClickedProcId(row.original.proc_id);
        },
        sx: {
          cursor: "pointer",
          background: () => {
            return clickedProcId === Number(row.original.proc_id)
              ? "#dee2e6 !important"
              : "#F8F7FA !important";
          },
        },
      }),
      muiTablePaperProps: {
        sx: {
          width: "100%",
          height: "100%",
          background: "#fff",
          "& .css-qc99f7": {
            position: "absolute !important",
            bottom: "30px",
            borderBottom: "1px solid #d6d8df",
          },
        },
      },
      renderEditRowDialogContent: ({ internalEditComponents, row, table }) => (
        <>
          <EditRowDialog
            table={table}
            row={row}
            initialData={row.original}
            handleUpdateOrder={handleUpdateProcess}
          />
        </>
      ),
      muiTableHeadProps: {
        sx: {
          "& tr": {
            // borderBottom: "2px solid rgba(224, 224, 224, 1)",
            // background: "black",
            // color: "white",
            // "& th": {
            //   borderRight: "2px solid white",
            // },
            borderRadius: "12px",
            background: "#f3f6f9",
            color: "#464e5f",
            "& th": {
              fontWeight: "500 !important",
              color: "black",
              padding: "0.5rem",
              fontSize: "15px",
              "&:first-of-type div": {
                justifyContent: "center",
              },
            },
          },
        },
      },
      muiTableBodyCellProps: {
        sx: {
          color: "black",
          fontWeight: "500",
          fontSize: "14px",
        },
      },
      renderTopToolbarCustomActions: ({ table }) => (
        <Box
          sx={{
            height: "40px",
            width: "150px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            fontSize: "22px",
            background: "#f8f7fa",
            fontWeight: "700",
            gap: "1rem",
            paddingLeft: "0.53rem",
          }}
        >
          <StackPerspective3 />
          공정{" "}
          <Tooltip
            title="이동"
            sx={{ cursor: "pointer" }}
            onClick={() =>
              goToHelpLink(
                "http://esehelp.cafe24.com/scheduler#h-8-2-%EA%B3%B5%EC%A0%95-%EA%B4%80%EB%A6%AC-%EC%98%81%EC%97%AD"
              )
            }
          >
            <HelpOutlineIcon />
          </Tooltip>
        </Box>
      ),
      paginationDisplayMode: "pages",
      renderToolbarInternalActions: ({ table }) => (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <TextField
            placeholder="검색어를 입력하세요"
            variant="outlined"
            sx={{
              width: "70%",
              marginBottom: "0px",
            }}
            size="small"
            value={procSearchText}
            onChange={onItemSearchTextChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <AddButton
            onClick={() => {
              if (!clickedItemId) {
                openSnackbar(itemClickFirst, "info");
                return;
              }
              table.setCreatingRow(true);
            }}
            // sx={{
            //   border: "1px solid #847c87",
            //   color: "black",
            //   boxSizing: "border-box",
            //   "&: hover": {
            //     border: "1px solid #161029 !important",
            //     background: "#161029",
            //     color: "white",
            //   },
            // }}
          />
        </div>
      ),
      positionActionsColumn: "last",
      renderCreateRowDialogContent: ({
        table,
        row,
        internalEditComponents,
      }) => (
        <CreateProcessDialog
          table={table}
          handleCreateProcess={handleCreateProcess}
        />
      ),
      muiBottomToolbarProps: {
        sx: {
          position: "absolute !important",
          bottom: "42px",
          left: "50%",
          width: "inherit",
          transform: "translate(-50%, -50%)",
          "& .MuiTablePagination-root .MuiBox-root": {
            display: "none !important",
          },
        },
      },
      // renderRowActions: ({ row, table }) => (
      //   <Box sx={{ display: "flex" }}>
      //     <Tooltip title="Edit">
      //       <IconButton onClick={() => table.setEditingRow(row)}>
      //         <Pen01 />
      //       </IconButton>
      //     </Tooltip>
      //     <Tooltip
      //       title="Delete"
      //       onClick={() => openDeleteConfirm(row.original.proc_id)}
      //     >
      //       <IconButton color="error">
      //         <Trash />
      //       </IconButton>
      //     </Tooltip>
      //   </Box>
      // ),
    },
    [tableData, columns, openDeleteConfirm]
  );
  useEffect(() => {
    if (procData) {
      queryClient.setQueryData(
        [updateStatus, "/getInsightwinProcessList"],
        procData
      );
    }
  }, [procData, queryClient, updateStatus]);

  return (
    <MaterialReactTable
      muiTableBodyProps={{
        sx: { overflow: "visible" },
      }}
      table={table}
    />
  );
}

export default Process;
